import cn from "classnames"
import { useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"

import { Form as AntdForm, Spin } from "@/atoms"
import { isSubmitAllowedOnElement } from "@/helpers/utils"
import { useForm } from "@/organisms/Form/hooks/useForm"
import { ActionsSectionForm } from "./components/ActionsSectionForm"
import { Aside } from "./components/Aside"
import { ConditionsSectionForm } from "./components/ConditionsSectionForm"
import { DetailsSectionForm } from "./components/DetailsSectionForm"
import { Fallback } from "./components/Fallback"
import { Footer } from "./components/Footer"
import { FormPage } from "./components/FormPage"
import { Header } from "./components/Header"
import { RadioGroup } from "./components/RadioGroup"
import { RadioGroupSectionFormTemplate } from "./components/RadioGroupSectionFormTemplate"
import { ResourcesSectionForm } from "./components/ResourcesSectionForm"
import { SectionFormTemplate } from "./components/SectionFormTemplate"

import styles from "./Form.module.scss"

import type { FormProps } from "./Form.types"

export const Form = <V = any,>(props: FormProps<V>) => {
  const {
    loading,
    className,
    layout = "vertical",
    onFinish,
    navigateOnFinished,
    ...rest
  } = props
  const formCtx = useForm()
  const { form, custom } = formCtx
  const navigate = useNavigate()
  const redirect = useRef(false)

  useEffect(() => {
    if (redirect.current && !custom.isDirty && navigateOnFinished)
      navigate(navigateOnFinished)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [custom.isDirty])

  const showSpinner = loading || !form || !custom

  useEffect(() => {
    const { id } = props

    if (showSpinner || !id) return

    const handleOnKeyboard = (event: KeyboardEvent) => {
      const element = isSubmitAllowedOnElement(event)

      if (!element) return

      const submitButton = element
        .closest("#outlet-layout-container")
        ?.querySelector("#submit-button-footer-form") as HTMLButtonElement

      if (submitButton) submitButton.click()
    }

    const form = document.getElementById(id)

    if (!form) return

    form.addEventListener("keydown", handleOnKeyboard, true)

    return () => form.removeEventListener("keydown", handleOnKeyboard, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSpinner])

  if (showSpinner) {
    return <Spin className={styles.loader} />
  }

  return (
    <AntdForm
      form={form}
      className={cn(styles.form, className)}
      layout={layout}
      onFieldsChange={custom.onFieldsChange}
      onFinish={async (values) => {
        try {
          await onFinish?.(values, formCtx)

          custom.setIsDirty(false)

          if (navigateOnFinished) redirect.current = true
        } catch {
          // should be handled by the caller
        }
      }}
      {...rest}
    >
      {props.children}
    </AntdForm>
  )
}

Form.Section = {
  Template: SectionFormTemplate,
  Details: DetailsSectionForm,
  Resources: ResourcesSectionForm,
  Conditions: ConditionsSectionForm,
  Actions: ActionsSectionForm,
  RadioGroupTemplate: RadioGroupSectionFormTemplate
}

Form.RadioGroup = RadioGroup

Form.Aside = Aside

Form.Header = Header

Form.Footer = Footer

Form.Page = FormPage

Form.Fallback = Fallback

Form.useForm = useForm

Form.Item = AntdForm.Item

Form.List = AntdForm.List

Form.useWatch = AntdForm.useWatch

Form.ErrorList = AntdForm.ErrorList
